import Layout from "../components/Layout";
import { useRouter } from "@tanstack/react-router";
import {
  BookOpen,
  CheckCircle2,
  MapPin,
  Shield,
  Smile,
  Star,
  Target,
  Users,
} from "lucide-react";
import { motion } from "motion/react";
import LeadForm from "../components/LeadForm";

const benefits = [
  {
    icon: Smile,
    title: "Confidence",
    desc: "Kids learn to believe in themselves through consistent progress and positive reinforcement.",
  },
  {
    icon: Target,
    title: "Discipline",
    desc: "Structured classes teach children to follow instructions, stay on task, and build good habits.",
  },
  {
    icon: BookOpen,
    title: "Focus",
    desc: "Training challenges kids to pay attention and be present, skills that carry over into school and life.",
  },
  {
    icon: Star,
    title: "Respect",
    desc: "Children learn to respect their instructors, training partners, and themselves from day one.",
  },
  {
    icon: Shield,
    title: "Anti-Bullying Skills",
    desc: "We teach kids how to handle conflict with confidence and composure, not aggression.",
  },
];

const structurePoints = [
  {
    title: "Structured classes with clear progressions",
    desc: "Kids always know what to expect and can track their own improvement.",
  },
  {
    title: "Fun, game-based learning",
    desc: "We mix technique with movement games so training never feels like a chore.",
  },
  {
    title: "Small group instruction",
    desc: "Our instructors give personal attention so every child gets the support they need.",
  },
];

const parentCards = [
  {
    icon: Shield,
    title: "Safe Environment",
    desc: "Our facility and coaching standards are designed with your child's safety and comfort in mind. We maintain a respectful, bully-free training culture.",
  },
  {
    icon: Users,
    title: "Positive Role Models",
    desc: "Our instructors are experienced, patient, and genuinely invested in every child's growth — inside and outside the gym.",
  },
  {
    icon: CheckCircle2,
    title: "Real Skill Development",
    desc: "Your child won't just play — they'll develop real Jiu Jitsu skills, improve their coordination, and build the mental tools to handle challenges with confidence.",
  },
];

const serviceAreas = ["Eugene", "Thurston", "Glenwood", "Fairmont"];

export default function KidsJiuJitsuPage() {
  const router = useRouter();

  return (
    <Layout>
      <title>
        Kids Jiu Jitsu Classes in Springfield, OR | Superhero Jiu Jitsu Academy
      </title>
      <meta
        name="description"
        content="Enroll your Kids in No-Gi Jiu-Jitsu at Superhero Jiu Jitsu Academy in Springfield, OR. We focus on building confidence, focus, discipline, and respect in each session to help your child build problem-solving skills and composure under pressure."
      />

      {/* ── HERO SECTION ── */}
      <section
        className="relative min-h-[92vh] flex items-center"
        data-ocid="kids-hero"
      >
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage:
              "url('/assets/images/kids jiu-jitsu hero image.jpg')",
          }}
          aria-hidden="true"
        />
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              "radial-gradient(circle at 30% 50%, oklch(0.68 0.16 82) 0%, transparent 60%), radial-gradient(circle at 80% 20%, oklch(0.68 0.16 82 / 0.4) 0%, transparent 50%)",
          }}
          aria-hidden="true"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to right, oklch(0.15 0.045 255 / 0.85) 0%, oklch(0.15 0.045 255 / 0.5) 55%, oklch(0.15 0.045 255 / 0.15) 100%)",
          }}
          aria-hidden="true"
        />
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-accent" />

        <div className="relative container mx-auto px-4 sm:px-6 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left — copy */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            >
              <span className="inline-block text-xs font-display font-semibold tracking-widest uppercase text-gold mb-4 border border-gold/40 px-3 py-1 rounded-full">
                Ages 4–10 · Springfield, OR
              </span>
              <h1 className="font-display font-black text-4xl sm:text-5xl md:text-6xl text-hero-foreground leading-tight text-balance mb-5">
                Kids Jiu Jitsu in{" "}
                <span className="text-gold">Springfield, OR</span>
                <span className="block text-3xl sm:text-4xl md:text-5xl mt-1">
                  (Ages 4–10)
                </span>
              </h1>
              <p className="text-hero-foreground font-body text-lg leading-relaxed max-w-lg mb-8">
                Help your child build confidence, discipline, and focus in a
                fun, structured environment.
              </p>
              <div className="flex flex-wrap gap-3">
                {[
                  "Safe & Welcoming",
                  "No Experience Needed",
                  "Expert Instructors",
                ].map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gold/20 border border-gold/40 text-sm font-body font-medium text-hero-foreground"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-gold flex-shrink-0" />
                    {tag}
                  </span>
                ))}
              </div>
            </motion.div>

            {/* Right — lead capture form */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
            >
              <div
                className="bg-primary/80 backdrop-blur-sm border border-gold/30 rounded-2xl p-6 sm:p-8 shadow-elevated relative overflow-hidden"
                data-ocid="kids-hero-form"
              >
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-accent" />
                <h2 className="font-display font-bold text-xl text-hero-foreground mb-1">
                  Book a Free Class
                </h2>
                <p className="text-hero-foreground font-body text-sm mb-6 opacity-80">
                  No experience needed. Let your child try a class on us.
                </p>

                <LeadForm />

              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Gold divider */}
      <div className="h-1 bg-gradient-accent" />

      {/* ── INTRO SECTION ── */}
      <section className="bg-background py-16 px-4" data-ocid="kids-intro">
        <div className="container mx-auto max-w-5xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <span className="inline-block text-xs font-display font-semibold tracking-widest uppercase text-gold mb-3">
                Built for Kids Ages 4–10
              </span>
              <h2 className="font-display font-black text-3xl sm:text-4xl text-foreground leading-tight mb-6 text-balance">
                A Positive, Structured Learning Environment
              </h2>
              <div className="space-y-4 text-muted-foreground font-body text-base leading-relaxed">
                <p>
                  Our kids Jiu Jitsu program is designed to give children ages
                  4–10 a safe, welcoming space where they can learn, grow, and
                  thrive. Every class is structured to be age-appropriate,
                  engaging, and fun — so kids stay focused and look forward to
                  training.
                </p>
                <p>
                  Through No-Gi Jiu Jitsu, kids develop real skills while
                  building character. Our instructors create an environment
                  where every child feels supported, challenged, and celebrated
                  for their progress — no matter their starting point.
                </p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="relative rounded-2xl overflow-hidden shadow-elevated"
            >
              <img
                src="/assets/images/Superhero Jiu-Jitsu Acadmey_Kids Jiu-jitsu.jpg"
                alt="Instructor coaching a student during Jiu Jitsu class"
                className="w-full object-cover aspect-[4/3]"
                loading="lazy"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── BENEFITS SECTION ── */}
      <section
        className="bg-muted/40 py-16 px-4 border-t border-border"
        data-ocid="kids-benefits"
      >
        <div className="container mx-auto max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-block text-xs font-display font-semibold tracking-widest uppercase text-gold mb-3">
              Character Development
            </span>
            <h2 className="font-display font-black text-3xl sm:text-4xl text-foreground mb-12 text-balance">
              What Your Child Will Gain
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {benefits.map(({ icon: Icon, title, desc }, i) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.08 }}
                className="bg-card rounded-2xl border border-border p-6 flex flex-col items-center text-center gap-4 shadow-card hover:shadow-elevated transition-smooth hover:-translate-y-0.5"
              >
                <span className="w-12 h-12 rounded-xl bg-gradient-accent flex items-center justify-center shadow-gold flex-shrink-0">
                  <Icon className="w-5 h-5 text-foreground" />
                </span>
                <div>
                  <h3 className="font-display font-bold text-base text-foreground mb-2">
                    {title}
                  </h3>
                  <p className="font-body text-sm text-muted-foreground leading-snug">
                    {desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PROGRAM STRUCTURE SECTION ── */}
      <section
        className="bg-background py-16 px-4 border-t border-border"
        data-ocid="kids-structure"
      >
        <div className="container mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-10"
          >
            <span className="inline-block text-xs font-display font-semibold tracking-widest uppercase text-gold mb-3">
              How We Teach
            </span>
            <h2 className="font-display font-black text-3xl sm:text-4xl text-foreground text-balance mb-4">
              Age-Appropriate, Safe, and Engaging
            </h2>
            <p className="text-muted-foreground font-body text-base leading-relaxed max-w-2xl mx-auto">
              Every class is designed with the developmental needs of children
              ages 4–10 in mind. We use games, drills, and technique practice to
              keep kids engaged while teaching real Jiu Jitsu fundamentals.
              Safety is always our top priority.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="bg-hero rounded-2xl overflow-hidden shadow-elevated"
          >
            <div className="h-1 bg-gradient-accent" />
            <div className="p-8 sm:p-10">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {structurePoints.map(({ title, desc }, i) => (
                  <motion.div
                    key={title}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: 0.2 + i * 0.1 }}
                    className="text-center"
                  >
                    <span className="font-display font-black text-4xl text-gold opacity-60 block mb-3">
                      0{i + 1}
                    </span>
                    <h3 className="font-display font-bold text-base text-hero-foreground mb-3">
                      {title}
                    </h3>
                    <p className="font-body text-sm text-hero-foreground leading-relaxed opacity-80">
                      {desc}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── PARENT SECTION ── */}
      <section
        className="bg-muted/40 py-16 px-4 border-t border-border"
        data-ocid="kids-parents"
      >
        <div className="container mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-10"
          >
            <span className="inline-block text-xs font-display font-semibold tracking-widest uppercase text-gold mb-3">
              For Parents
            </span>
            <h2 className="font-display font-black text-3xl sm:text-4xl text-foreground text-balance mb-4">
              Why Parents Choose This Program
            </h2>
            <p className="text-muted-foreground font-body text-base leading-relaxed max-w-2xl mx-auto">
              We understand that choosing a program for your child is a big
              decision. Here's what parents tell us matters most:
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {parentCards.map(({ icon: Icon, title, desc }, i) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.1 }}
                className="bg-card rounded-2xl border border-border p-6 flex flex-col gap-4 shadow-card hover:shadow-elevated transition-smooth"
              >
                <span className="w-11 h-11 rounded-xl bg-gradient-accent flex items-center justify-center shadow-gold flex-shrink-0">
                  <Icon className="w-5 h-5 text-foreground" />
                </span>
                <div>
                  <h3 className="font-display font-bold text-base text-foreground mb-2">
                    {title}
                  </h3>
                  <p className="font-body text-sm text-muted-foreground leading-relaxed">
                    {desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Testimonial */}
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="bg-card rounded-2xl border border-gold/30 p-6 shadow-card text-center max-w-xl mx-auto"
          >
            <div className="flex gap-1 justify-center mb-3">
              {[1, 2, 3, 4, 5].map((s) => (
                <Star key={s} className="w-4 h-4 text-gold fill-current" />
              ))}
            </div>
            <p className="font-body text-base text-foreground italic leading-relaxed mb-3">
              "My son's confidence has grown so much since joining. He loves
              coming to class and can't stop talking about what he learns!"
            </p>
            <span className="font-display font-semibold text-sm text-muted-foreground">
              — Parent of a 7-year-old
            </span>
          </motion.div>
        </div>
      </section>

      {/* ── CTA SECTION ── */}
      <section className="bg-hero py-20 px-4 text-center" data-ocid="kids-cta">
        <div className="container mx-auto max-w-2xl">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-block text-xs font-display font-semibold tracking-widest uppercase text-gold mb-3 border border-gold/40 px-3 py-1 rounded-full">
              First Class Is Free
            </span>
            <h2 className="font-display font-black text-3xl sm:text-4xl md:text-5xl text-hero-foreground leading-tight mb-4 text-balance">
              Give Your Child the Gift of Confidence
            </h2>
            <p className="text-hero-foreground font-body text-lg leading-relaxed mb-8 max-w-lg mx-auto">
              Book a free trial class today. No experience needed — just bring
              your child and we'll take care of the rest.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                type="button"
                onClick={() => router.navigate({ to: "/contact-us" })}
                className="inline-flex items-center justify-center px-8 py-4 rounded-xl font-display font-bold text-base bg-gradient-accent text-foreground shadow-gold hover:opacity-90 active:scale-[0.98] transition-smooth cursor-pointer"
                data-ocid="kids-cta-button"
              >
                Book a Free Class
              </button>
              <a
                href="tel:+15416003371"
                className="inline-flex items-center justify-center px-8 py-4 rounded-xl font-display font-bold text-base border-2 border-primary-foreground/60 text-hero-foreground hover:bg-primary-foreground/10 transition-smooth cursor-pointer"
                data-ocid="kids-cta-phone"
              >
                Call (541) 600-3371
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── LOCAL SEO SECTION ── */}
      <section
        className="bg-background py-10 px-4 border-t border-border"
        data-ocid="kids-local-seo"
      >
        <div className="container mx-auto max-w-3xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center justify-center gap-2 mb-4">
              <MapPin className="w-4 h-4 text-gold flex-shrink-0" />
              <span className="font-display font-semibold text-sm text-muted-foreground uppercase tracking-wider">
                Serving Springfield &amp; Surrounding Areas
              </span>
            </div>
            <p className="font-body text-muted-foreground text-sm leading-relaxed max-w-2xl mx-auto mb-4">
              Superhero Jiu Jitsu Academy offers Kids Jiu Jitsu classes in
              Springfield, OR, proudly serving families in{" "}
              {serviceAreas.join(", ")}, and surrounding Lane County areas.
            </p>
            <address className="not-italic font-body text-sm text-muted-foreground mb-5">
              <span className="font-medium text-foreground">
                1485 Mohawk Blvd, Springfield, OR 97477
              </span>
              {" · "}
              <a
                href="tel:+15416003371"
                className="text-foreground font-medium hover:text-gold transition-colors-fast"
              >
                (541) 600-3371
              </a>
            </address>
            <div className="flex flex-wrap justify-center gap-2">
              {["Springfield", ...serviceAreas].map((area) => (
                <span
                  key={area}
                  className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-muted border border-border text-sm font-body text-muted-foreground"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-gold flex-shrink-0" />
                  {area}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
